import React from 'react';
import {
  PostDetails_ActivePost_Fragment,
  PostDetails_DeletedPost_Fragment,
  PostDetailsFragment,
  PostOrder,
  PrivatePostsQueryResponse,
  usePrivatePostsQuery,
  UserPostsQueryResponse,
  useUserPostsQuery,
} from '../__generated__/api.graphql';
import {
  CLINICIANS_AGENT_USERNAME,
  MEMBERS_AGENT_USERNAME,
} from '../constants';
import {
  useActiveProjectCohortId,
  useActiveProjectId,
  useActiveUserId,
} from '@lifeomic/phc-web-toolkit/src/hooks';
import { QueryResult } from 'react-apollo';

const usePostHistory = (
  projectId: string,
  cohortId: string,
  userId: string,
  patientId: string,
  patientUsername: string,
  isMemberChatHistory?: boolean,
):
  | QueryResult<UserPostsQueryResponse>
  | QueryResult<PrivatePostsQueryResponse> => {
  const userPostResult = useUserPostsQuery({
    variables: {
      userIds: [MEMBERS_AGENT_USERNAME, patientUsername],
      projectId: projectId,
      cohortId: cohortId,
      filter: {
        order: PostOrder.NEWEST,
      },
    },
    skip: !isMemberChatHistory,
  }) as QueryResult<UserPostsQueryResponse>;

  const privatePostResult = usePrivatePostsQuery({
    variables: {
      userIds: [
        userId,
        CLINICIANS_AGENT_USERNAME,
        `scope?subject=${patientId}`,
      ],
      filter: {
        order: PostOrder.NEWEST,
      },
    },
    skip: isMemberChatHistory,
  }) as QueryResult<PrivatePostsQueryResponse>;
  return isMemberChatHistory ? userPostResult : privatePostResult;
};

export const useChatHistory = ({
  patientId,
  isMemberChatHistory,
  patientUsername,
}: {
  patientId: string;
  isMemberChatHistory?: boolean;
  patientUsername: string;
}) => {
  const userId = useActiveUserId();
  const projectId = useActiveProjectId();
  const cohortId = useActiveProjectCohortId();
  const [posts, setPosts] = React.useState<PostDetailsFragment[]>([]);
  const initialPageLoaded = React.useRef(false);
  const [endCursor, setEndCursor] = React.useState<string | undefined | null>(
    null,
  );
  const { data, loading, fetchMore, networkStatus, startPolling } =
    usePostHistory(
      projectId,
      cohortId,
      userId,
      patientId,
      patientUsername,
      isMemberChatHistory,
    );

  const findNewPosts = React.useCallback(() => {
    if (!data) return;
    return mapResToPosts(data).posts.filter(
      (
        post:
          | PostDetails_ActivePost_Fragment
          | PostDetails_DeletedPost_Fragment,
      ) => !posts.find(displayPost => displayPost.id === post.id),
    );
  }, [data, posts]);

  React.useEffect(() => {
    if (data) {
      if (!initialPageLoaded.current) {
        setPosts(mapResToPosts(data).posts);
        const maybeCursor =
          'privatePosts' in data
            ? data.privatePosts.pageInfo.endCursor
            : data.userPosts.pageInfo.endCursor;
        setEndCursor(maybeCursor);
        initialPageLoaded.current = true;
      } else {
        // update when posts changes (i.e. a new message is sent)
        const newPosts = findNewPosts();
        if (!newPosts?.length) return;
        setPosts(posts => newPosts.concat(posts));
      }
    }
  }, [data, findNewPosts]);

  const mapResToPosts = (
    queryResponse: PrivatePostsQueryResponse | UserPostsQueryResponse,
  ) => {
    type NodeType =
      | ({ __typename: 'ActivePost' } & PostDetails_ActivePost_Fragment)
      | ({ __typename: 'DeletedPost' } & PostDetails_DeletedPost_Fragment);
    type EdgeType = {
      __typename: 'PostEdge';
      node: NodeType;
    };
    const edges =
      'privatePosts' in queryResponse
        ? queryResponse.privatePosts.edges
        : queryResponse.userPosts.edges;
    const endCursor =
      'privatePosts' in queryResponse
        ? queryResponse.privatePosts.pageInfo.endCursor
        : queryResponse.userPosts.pageInfo.endCursor;

    const posts = edges
      .map((edge: EdgeType) => edge?.node)
      .filter((node: NodeType): node is PostDetailsFragment => !!node)
      .map((node: NodeType) => node);

    return { posts, endCursor };
  };

  const loadMore = async (): Promise<void> => {
    const params = isMemberChatHistory
      ? {
          variables: {
            userIds: [MEMBERS_AGENT_USERNAME, patientUsername],
            projectId: projectId,
            cohortId: cohortId,
            filter: {
              order: PostOrder.NEWEST,
            },
            after: endCursor,
          },
        }
      : {
          variables: {
            userIds: [
              userId,
              CLINICIANS_AGENT_USERNAME,
              `scope?subject=${patientId}`,
            ],
            filter: {
              order: PostOrder.NEWEST,
            },
            after: endCursor,
          },
        };

    const data = await fetchMore(params);

    const { posts: morePosts, endCursor: newEndCursor } = mapResToPosts(
      data.data,
    );
    setPosts(currentPosts => {
      currentPosts.push(...morePosts);
      return currentPosts;
    });
    setEndCursor(newEndCursor);
  };

  /** Eagerly pushes a new post into the posts array for faster UI responsiveness on sending a message. */
  const addEagerPost = (post: PostDetailsFragment) => {
    setPosts(posts => [post, ...posts]);
  };

  return {
    loading,
    fetchMore,
    loadMore,
    addEagerPost,
    posts,
    endCursor,
    networkStatus,
    userId,
    startPolling,
  };
};
